import React, { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import logger from "@/utils/logger";
import { isInTrialPeriod, getTrialDaysRemaining, getTrialHoursRemaining } from "@/utils/subscription-trial";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

type SubscriptionBadgeProps = {
  className?: string;
};

const SubscriptionBadge: React.FC<SubscriptionBadgeProps> = ({
  className = "",
}) => {
  const { user } = useAuth();
  const [hasSubscription, setHasSubscription] = useState<boolean | null>(null);
  const [planType, setPlanType] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const [subscriptionData, setSubscriptionData] = useState<any>(null);
  const [isInTrial, setIsInTrial] = useState<boolean>(false);
  const [trialDaysRemaining, setTrialDaysRemaining] = useState<number | null>(null);
  const [trialHoursRemaining, setTrialHoursRemaining] = useState<number | null>(null);

  useEffect(() => {
    // Don't check subscription if user is not logged in
    if (!user) {
      setIsLoading(false);
      setHasSubscription(false);
      return;
    }

    const checkSubscription = async () => {
      try {
        // Use the has_active_subscription RPC function we created
        // Using any type to bypass TypeScript limitations
        logger.info("Checking subscription status for user:", user.id);
        const { data: hasActive, error: subscriptionError } = await (
          supabase as any
        ).rpc("has_active_subscription", { p_user_id: user.id });

        logger.info("Subscription check result:", {
          hasActive,
          subscriptionError,
        });

        if (subscriptionError) {
          logger.error("Error checking subscription:", subscriptionError);
          setHasSubscription(false);
        } else {
          // The RPC function returns a boolean value
          setHasSubscription(!!hasActive);
          logger.info("Has active subscription:", !!hasActive);

          // If user has subscription, get details using the get_user_subscription RPC function
          if (hasActive) {
            logger.info("Getting subscription details for user:", user.id);
            const { data: details, error: detailsError } = await (
              supabase as any
            ).rpc("get_user_subscription", { p_user_id: user.id });

            logger.info("Subscription details result:", {
              details,
              detailsError,
            });

            if (detailsError) {
              logger.error(
                "Error fetching subscription details:",
                detailsError
              );
            } else if (details && details.length > 0) {
              const subscriptionInfo = details[0];
              logger.info(
                "Setting plan type to:",
                subscriptionInfo.plan_type || "PREMIUM"
              );
              setPlanType(subscriptionInfo.plan_type || "PREMIUM");
              setSubscriptionData(subscriptionInfo);
              
              // Check trial status - log full subscription info for debugging
              logger.info("Full subscription info:", subscriptionInfo);
              
              // The getTrialDaysRemaining function now intelligently handles database values
              // It will prioritize valid database values while ensuring a good user experience
              const inTrial = isInTrialPeriod(subscriptionInfo) || subscriptionInfo.is_in_trial === true;
              const daysRemaining = getTrialDaysRemaining(subscriptionInfo);
              
              // Log calculations for debugging
              logger.info("Trial calculations:", {
                isInTrialFromDB: subscriptionInfo.is_in_trial === true,
                isInTrialByFunction: isInTrialPeriod(subscriptionInfo),
                dbDaysRemaining: subscriptionInfo.days_remaining,
                finalDaysRemaining: daysRemaining,
                calculationMethod: "Using intelligent trial calculation"
              });
              
              // Calculate hours if we're in the last day of the trial
              const hoursRemaining = daysRemaining === 0 ? getTrialHoursRemaining(subscriptionInfo) : null;
              
              setIsInTrial(inTrial);
              setTrialDaysRemaining(daysRemaining);
              setTrialHoursRemaining(hoursRemaining);
              
              logger.info(
                "Final trial status:",
                { 
                  isInTrial: inTrial,
                  daysRemaining: daysRemaining,
                  hoursRemaining: hoursRemaining,
                  // Extra debug info
                  trialStart: subscriptionInfo.trial_start,
                  trialEnd: subscriptionInfo.trial_end,
                  status: subscriptionInfo.status
                }
              );
            } else {
              logger.info("No subscription details found");
            }
          }
        }
      } catch (error) {
        logger.error("Unexpected error checking subscription:", error);
        setHasSubscription(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkSubscription();
  }, [user]); // Re-run when user changes

  if (isLoading) {
    return null; // Don't show anything while loading
  }

  if (!hasSubscription) {
    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 ${className}`}
      >
        Basic
      </span>
    );
  }

  // User has an active subscription
  if (isInTrial && trialDaysRemaining !== null) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r from-blue-500 to-blue-400 text-white ${className}`}
            >
              <span>Premium Trial</span>
              {trialDaysRemaining === 0 && trialHoursRemaining !== null ? (
                <span className="bg-white/20 px-1 rounded-sm text-[10px]">{trialHoursRemaining}h</span>
              ) : (
                <span className="bg-white/20 px-1 rounded-sm text-[10px]">{trialDaysRemaining}d</span>
              )}
            </span>
          </TooltipTrigger>
          <TooltipContent className="p-2 text-xs max-w-[200px]">
            {trialDaysRemaining === 0 && trialHoursRemaining !== null ? (
              <p>Your trial ends in {trialHoursRemaining} hour{trialHoursRemaining !== 1 ? 's' : ''}.</p>
            ) : (
              <p>Your trial ends in {trialDaysRemaining} day{trialDaysRemaining !== 1 ? 's' : ''}.</p>
            )}
            <p className="mt-1">Enjoy full premium access during your trial period!</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // Regular premium subscription
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r from-amber-500 to-amber-300 text-white ${className}`}
    >
      {planType === "YEARLY" ? "Premium (Yearly)" : "Premium"}
    </span>
  );
};

export default SubscriptionBadge;
